import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import { ScoringMatrixTable } from '@ifs-gcp/components';
import type { ITemplateNode } from '@ifs-gcp/core';
import type { ScoringMatrixWidgetChild } from '@ifs-gcp/core/models';

import { loadJsonChecklist } from '@/hooks/useJsonTemplate';
import { useAuditStore } from '@/store/auditStore';
import { getTranslation } from '@/utils/translations';

interface WidgetScoringMatrixProps {
  element: ITemplateNode<ScoringMatrixWidgetChild>;
}

export const WidgetResultMatrix = observer(
  ({
    element: {
      props: { context: widgetContext, variant, checklistId, translations },
    },
  }: WidgetScoringMatrixProps) => {
    const {
      i18n: { language },
    } = useTranslation();
    const audit = useAuditStore((state) => state.audit)!;

    const moduleId = widgetContext[0];

    const checklistDefinition = useAsync(async () => {
      const result = await loadJsonChecklist(checklistId);

      if (!result) {
        throw new Error(
          `Error while loading checklist ${checklistId} for widget audit result in ${moduleId}`,
        );
      }

      return result;
    });

    const matrixResult = audit.modules.getById(moduleId).matrixResult || [];

    if (checklistDefinition.loading) {
      return null;
    }

    if (checklistDefinition.error || !checklistDefinition.value) {
      throw new Error(checklistDefinition?.error?.message);
    }

    const { levelsSetting, chaptersList: chapters, availableScores } = checklistDefinition.value;

    return (
      <ScoringMatrixTable
        language={language}
        variant={variant}
        matrixResult={matrixResult}
        availableScores={availableScores}
        levelsSetting={levelsSetting}
        chapters={chapters}
        texts={{
          header: getTranslation(translations.title, language),
          resultPerChapter: getTranslation(translations.per_chapter, language),
          noCalculationPossible: getTranslation(translations.not_possible, language),
        }}
      />
    );
  },
);
